import React, { useState, useEffect, useRef,useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import { Button } from "../../../Button/Button";
import { useCallback } from "react";
import { debounce } from "lodash";
import axios from "axios";
import { setQueryParam, getQueryParam, deleteQueryParam } from "../../Utils/utils";

export const InputSuggestion = forwardRef((props, ref) => {
    const { hasButton,
        isFullWidth,
        hasIcon,
        label,
        isSearch,
        hasLocation,
        className = null,
        passInputValue,
        } = props
    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);

    const dropdownRef = useRef()

    useEffect(() => {
        const locationParam = getQueryParam("location");
        const provinceParam = getQueryParam("province");
        const lat = getQueryParam("lat");
        const lng = getQueryParam("lng");

        if(locationParam){
            setInputValue(locationParam)
            passInputValue({
                location: {
                    "lat": lat,
                    "long": lng,
                },
                zoom: 12
            });
        }
        else if (provinceParam) {
            setInputValue(provinceParam);
            passInputValue({
                location: {
                    name: provinceParam,
                    type: "Province",
                    lat: lat,
                    long: lng,
                },
                zoom: 10
            });
        }
    }, [])

    useEffect(() => {
        const checkIfClickedOutside = e => {
          if (
            showSuggestions &&
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target)
          ) {
            setShowSuggestions(false)
          }
          else{
            if(inputValue.length >= 3 && dropdownRef.current.contains(e.target))
                setShowSuggestions(true)
          }
        }
        document.addEventListener("mousedown", checkIfClickedOutside)
        
        return () => {
          document.removeEventListener("mousedown", checkIfClickedOutside)
        }
    }, [showSuggestions])

    useEffect(() => {
        async function fetch(){
            if(inputValue.length >= 2 ){
                await loadData(inputValue)
            }
        }
        fetch();
    }, [inputValue])

    useEffect(() => {
        if(suggestions.length > 0){
            setShowSuggestions(true)
        }
        else{
            setShowSuggestions(false)
        }
    }, [suggestions])
    
    const loadData = useCallback(debounce(async (cityName) => {
        setSuggestions([])
        axios.get(`/umbraco/api/community/fetchCommunities?searchTerm=${cityName}`)
        .then(response => {
            setSuggestions(response.data);
        })

    }, 500), []);

    const handleInputChange = async (e) => {
        if(e.target.value == ""){
            deleteQueryParam("location");
        }
        setInputValue(e.target.value)
    }

    const handleClear = () => {
        clearLocationFilter();
    }

    const clearLocationFilter = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('province');
        url.searchParams.delete('location');
        window.history.replaceState({}, '', url);

        setInputValue("");
        setSuggestions([]);
        setShowSuggestions(false);

        passInputValue({
            location: {
                lat: "51.037861",
                long: "4.240528"
            },
            zoom: 9
        });
    };

    const suggestionSelectionHandler = suggestion => {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        params.delete('province');
        params.delete('location');

        if (suggestion.type === "Province"){
            url.searchParams.set("province", suggestion.name)
        } else {
            url.searchParams.set("location", suggestion.name)
        }

        window.history.replaceState({}, '', url);

        setInputValue(suggestion.name)
        setShowSuggestions(false)
        setSuggestions([]);
        const zoom = suggestion.type === "Province" ? 10 : 12;
        passInputValue({
            location: suggestion,
            zoom
        })
    }

    const handleLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(function(position){
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;
                setInputValue("Jouw locatie");
                const myLocation = {
                    lat:String(latitude), 
                    long:String(longitude)
                }
                passInputValue({
                    location: myLocation,
                    zoom:12
                })
            }) ;
        } else {
            console.log("Geolocation is not supported by this browser.");
        }
    }

    const resetFilter = () => {
        clearLocationFilter();
    }

    useImperativeHandle(ref, () => ({
        resetFilter,
    }));

    return (
        <div
            ref={dropdownRef}
            className={`c-input ${hasButton ? 'c-input--has-button' : ''} ${isFullWidth ? 'c-input--full-width' : ''} ${className && className}`}            >
            <input 
                placeholder={label} 
                id="input" 
                className="c-input__field" 
                onChange={e => handleInputChange(e)}
                value={inputValue}
                autoComplete="off"    
            >        
            </input>
            {label && <label htmlFor="input" className="c-input__label">{label}</label>}
            <div className="c-input__right">
                {hasLocation && inputValue !== '' ? (
                    <i onClick={handleClear} className="c-input__right__close__location fal fa-times-circle"></i>
                ) : (!isSearch && !hasButton && hasIcon && hasLocation) ? (
                    <div onClick={handleLocation} className="c-input-icon__container">
                        <i className="fa-solid fa-location-crosshairs c-input__icon"></i>
                    </div>
                ) : null}
                
                {(hasButton) && (
                    <Button buttonType='primary' size='small' hasIcon={true} iconInFront={true} label='zoeken' isIcon={false} icon={props.buttonIcon && props.buttonIcon}></Button>
                )}
                {(isSearch && !hasButton && !inputValue) && (
                    <i className="fal fa-search"></i>
                )}
            </div>

            {showSuggestions ? (
                <div className="c-input-suggestion__container">
                    <ul className="c-input-suggestion__ul">
                        {suggestions.map( suggestion => {
                            return(
                                <li 
                                    className="c-input-suggestion__li"
                                    key={suggestion.value + suggestion.type}
                                    onClick={() => suggestionSelectionHandler(suggestion)}
                                >
                                    {suggestion.type === "Province"
                                        ? `Provincie: ${suggestion.name}`
                                        : `${suggestion.value} ${suggestion.name}`}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ):
            (
                <span></span>
            )
            }
            </div>
    );
});

InputSuggestion.propTypes = {
  /**
   * does the input have a button?
   */
  hasButton: PropTypes.bool.isRequired,
  /**
   * is the input a search input?
   */
  isSearch: PropTypes.bool.isRequired,
  /**
   * does the input have an icon?
   */
  hasIcon: PropTypes.bool.isRequired,
  /**
   * does the input have full width?
   */
  isFullWidth: PropTypes.bool.isRequired,
  /**
   * label for the input?
   */
  label: PropTypes.string.isRequired,
};

InputSuggestion.defaultProps = {
  hasButton: false,
  isSearch: false,
  hasIcon: true,
  isFullWidth: false,
  label: 'Zoek een sport of plaats',
};
